<script setup lang="ts">
    import { ref, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import EchoButton from '@/js/components/EchoButton.vue';
    import SettingsPageLayout from '@/js/components/settings/SettingsPageLayout.vue';
    import InputLabel from '@/js/components/InputLabel.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import { __ } from '@/js/stores/expressions';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import { useMutationCreateTenant } from '@/js/services/api/backOffice/backOfficeQueries';
    import { CheckIcon, XIcon, TriangleAlertIcon, InfoIcon } from '@lucide/vue';

    const router = useRouter();
    const successDialogStore = useSuccessDialog();

    const { mutateAsync: createTenantMutation, isPending } = useMutationCreateTenant();

    const DEFAULT_DB_KEY = 'SharedDb';

    const name = ref('');
    const dbKey = ref(DEFAULT_DB_KEY);
    const warnings = ref<string[]>([]);

    const canSubmit = computed(() => !!name.value.trim() && !!dbKey.value.trim());

    const submit = async () => {
        if (!canSubmit.value || isPending.value) return;
        const tenantName = name.value.trim();
        warnings.value = [];
        try {
            const result = await createTenantMutation({ name: tenantName, dbKey: dbKey.value.trim() });
            name.value = '';
            dbKey.value = DEFAULT_DB_KEY;
            warnings.value = result.warnings;
            successDialogStore.openDialog({
                message: __('back-office-create-tenant-success', { name: tenantName }),
            });
        } catch {
            // Error displayed via axios error message config in backOfficeApi.ts
        }
    };

    const cancel = () => {
        router.push('/back-office/overview');
    };
</script>
<template>
    <SettingsPageLayout>
        <template #header>
            <h1 class="heading-lg-light">
                {{ __('back-office-create-tenant') }}
            </h1>
        </template>
        <template #content>
            <form class="w-lg max-w-full" @submit.prevent="submit">
                <FormGroup>
                    <div>
                        <InputLabel for="tenantName">
                            {{ __('back-office-create-tenant-name') }}
                        </InputLabel>
                        <TextInput
                            id="tenantName"
                            v-model="name"
                            :width="TextInputWidth.Full"
                        />
                    </div>
                    <div>
                        <InputLabel for="tenantDbKey">
                            {{ __('back-office-create-tenant-db-key') }}
                        </InputLabel>
                        <TextInput
                            id="tenantDbKey"
                            v-model="dbKey"
                            :width="TextInputWidth.Full"
                        />
                        <p class="mt-2 flex gap-2 max-w-lg text-body text-gray-medium">
                            <InfoIcon class="size-4 shrink-0 mt-0.5" />
                            {{ __('back-office-create-tenant-db-key-info') }}
                        </p>
                    </div>
                </FormGroup>
                <div class="flex gap-3 mt-6">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        :disabled="!canSubmit"
                        :loading="isPending"
                    >
                        {{ __('save') }}
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                    <EchoButton type="button" variant="secondary" @click="cancel">
                        {{ __('cancel') }}
                        <template #icon-right><XIcon /></template>
                    </EchoButton>
                </div>
                <div
                    v-if="warnings.length > 0"
                    class="mt-6 flex flex-col gap-2 rounded-lg border border-sentiment-negative bg-sentiment-negative/10 p-4 max-w-lg"
                >
                    <div class="flex gap-3">
                        <TriangleAlertIcon class="size-5 shrink-0 text-sentiment-negative mt-0.5" />
                        <p class="text-body-bold text-sentiment-negative">{{ __('back-office-create-tenant-warnings') }}</p>
                    </div>
                    <ul class="ml-8 list-disc text-body text-sentiment-negative">
                        <li v-for="(warning, i) in warnings" :key="i">{{ warning }}</li>
                    </ul>
                </div>
            </form>
        </template>
    </SettingsPageLayout>
</template>
